// src\components\admin\ProjectCard.jsx
import React, { useState } from 'react';
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiCalendar,
  FiStar,
  FiTag,
  FiHeart,
  FiExternalLink,
  FiImage,
  FiShare2,
  FiMousePointer,
  FiCheckSquare,
  FiSquare,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';

function ProjectCard({
  project,
  onEdit,
  onDelete,
  onPreview,
  isSelected = false,
  onSelect = null,
  showSelection = true,
}) {
  const { actualTheme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const getOptimizedImageUrl = (url, width = 400) => {
    if (!url) return '';
    if (url.includes('cloudinary.com'))
      return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto/`);
    return url;
  };

  const getThumbnailUrl = () => {
    return project.thumbnailUrl || project.images?.[0]?.url || project.imageUrl;
  };

  const getImageCount = () => {
    return project.images?.length || (project.imageUrl ? 1 : 0);
  };

  const formatDateTime = dateString => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    const now = new Date();

    // Get date components for comparison (local timezone)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const postDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    // Calculate difference in days (local timezone)
    const diffTime = today.getTime() - postDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Format time (12-hour format with AM/PM)
    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };
    const timeString = date.toLocaleTimeString('en-US', timeOptions);

    // Check if it's today
    if (diffDays === 0) {
      return `Today at ${timeString}`;
    }

    // Check if it's yesterday
    if (diffDays === 1) {
      return `Yesterday at ${timeString}`;
    }

    // If less than 7 days ago
    if (diffDays < 7 && diffDays > 1) {
      return `${diffDays}d ago at ${timeString}`;
    }

    // If less than 30 days ago, show weeks
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks}w ago at ${timeString}`;
    }

    // If less than 365 days ago, show months
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months}mo ago at ${timeString}`;
    }

    // Otherwise show full date + time
    const dateOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    const formattedDate = date.toLocaleDateString('en-US', dateOptions);
    return `${formattedDate} at ${timeString}`;
  };

  const getTypeIcon = type => {
    switch (type) {
      case 'project':
        return '📁';
      case 'random':
        return '🎲';
      default:
        return '📄';
    }
  };

  // Handle selection click with stop propagation
  const handleSelectionClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onSelect) onSelect();
  };

  return (
    <div
      className={`group relative rounded-2xl overflow-hidden transition-all duration-300 border hover:shadow-xl hover:-translate-y-1 ${
        isSelected
          ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-transparent'
          : ''
      }`}
      style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Selection Checkbox - Top Left */}
      {showSelection && onSelect && (
        <div
          className="absolute top-3 left-3 z-20"
          style={{ pointerEvents: 'auto' }}
        >
          <button
            type="button"
            onClick={handleSelectionClick}
            className="p-1 rounded-md transition-all hover:scale-110 focus:outline-none"
            style={{
              backgroundColor: isSelected ? 'var(--text)' : 'rgba(0,0,0,0.6)',
              color: isSelected ? 'var(--bg)' : 'white',
              backdropFilter: 'blur(4px)',
              border: 'none',
              cursor: 'pointer',
            }}
            title={isSelected ? 'Deselect' : 'Select'}
          >
            {isSelected ? (
              <FiCheckSquare className="w-4 h-4" />
            ) : (
              <FiSquare className="w-4 h-4" />
            )}
          </button>
        </div>
      )}

      {/* Image Container */}
      <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          </div>
        )}
        <img
          src={getOptimizedImageUrl(getThumbnailUrl(), 500)}
          alt={project.title}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />

        {/* Featured Badge */}
        {project.featured && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-yellow-500 text-white shadow-lg z-10">
            <FiStar className="w-3 h-3" /> Featured
          </div>
        )}

        {/* Image Count Badge */}
        {getImageCount() > 1 && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 bg-black/70 text-white backdrop-blur-sm z-10">
            <FiImage className="w-3 h-3" />
            <span>{getImageCount()}</span>
          </div>
        )}

        {/* Type Badge */}
        <div className="absolute bottom-3 left-3 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 bg-black/70 text-white backdrop-blur-sm z-10">
          <span>{getTypeIcon(project.type)}</span>
          <span>{project.type || 'project'}</span>
        </div>

        {/* Hover Actions Overlay */}
        <div
          className={`absolute inset-0 flex items-center justify-center gap-3 transition-all duration-300 z-10 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
        >
          <button
            type="button"
            onClick={() => onPreview(project)}
            className="p-2.5 rounded-full bg-white text-black transition-all transform hover:scale-110 hover:shadow-lg"
            title="Preview"
          >
            <FiEye className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onEdit(project)}
            className="p-2.5 rounded-full bg-white text-black transition-all transform hover:scale-110 hover:shadow-lg"
            title="Edit"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(project)}
            className="p-2.5 rounded-full bg-white text-red-500 transition-all transform hover:scale-110 hover:shadow-lg"
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3
            className="font-semibold text-lg line-clamp-1 flex-1"
            style={{ color: 'var(--text)' }}
          >
            {project.title}
          </h3>
          {project.featured && (
            <FiStar className="w-4 h-4 text-yellow-500 flex-shrink-0" />
          )}
        </div>

        {/* Stats - Only in content area */}
        <div
          className="flex flex-col gap-1 text-xs mb-2"
          style={{ color: 'var(--muted)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1">
                <FiCalendar className="w-3 h-3" />
                {formatDateTime(project.createdAt)}
              </span>
            </div>
            <span className="capitalize px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">
              {project.category}
            </span>
          </div>
          <div className="flex items-center gap-3 justify-end">
            <span className="flex items-center gap-0.5">
              <FiHeart className="w-3 h-3 text-red-400" />
              <span>{project.likes || 0}</span>
            </span>
            <span className="flex items-center gap-0.5">
              <FiEye className="w-3 h-3 text-blue-400" />
              <span>{project.views || 0}</span>
            </span>
            <span className="flex items-center gap-0.5">
              <FiMousePointer className="w-3 h-3 text-green-400" />
              <span>{project.clicks || 0}</span>
            </span>
            <span className="flex items-center gap-0.5">
              <FiShare2 className="w-3 h-3 text-purple-400" />
              <span>{project.shares || 0}</span>
            </span>
          </div>
        </div>

        <p
          className="text-sm line-clamp-2 mb-3"
          style={{ color: 'var(--muted)' }}
        >
          {project.description}
        </p>

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {project.tags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1 transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiTag className="w-2 h-2" /> {tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span
                className="px-2 py-0.5 rounded-full text-xs"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                +{project.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* View Live Link */}
        <a
          href={
            project.workType === 'illustration'
              ? `/illustrations/${project.id}`
              : `/portfolio/${project.id}`
          }
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity hover:underline"
          style={{ color: 'var(--muted)' }}
        >
          <FiExternalLink className="w-3 h-3" />
          View Live{' '}
          {project.workType === 'illustration' ? 'Illustration' : 'Project'}
        </a>
      </div>
    </div>
  );
}

export default ProjectCard;
